import { TActions } from '../__shared/shared.interfaces';
import { TDocument } from '../documents/documents.interfaces';
import { TPrimaryDetails } from '../users/users.interfaces';

// Assessment type enum (similar to SCORE_TYPE)
export enum EAssessmentType {
  EXAM = 'exam',
  TEST = 'test',
  ASSIGNMENT = 'assignment',
  PROJECT = 'project',
  QUIZ = 'quiz',
  MID_TERM = 'mid_term',
  FINAL = 'final',
}

// Assessment status enum
export enum EAssessmentStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  GRADED = 'graded',
  CANCELLED = 'cancelled',
}

// Individual assessment record (similar to TStudentScore)
export interface IAcademicsAssessment {
  assessmentId: string;
  name: string;
  description?: string;
  assessmentType: EAssessmentType;
  status: EAssessmentStatus;

  // Links to class/course
  classId?: number; // Optional - can be class-level or course-level
  courseId?: number; // Optional - can be class-level or course-level
  lessonPlanId?: number; // Optional - can be class-level or course-level

  // Assessment details
  maxScore: number;
  totalStudents?: number; // Number of students who took this assessment
  averageScore?: number; // Average score across all students
  averagePercentage?: number; // Average percentage

  // Dates
  scheduledDate?: string;
  conductedDate?: string;
  dueDate?: string;
  gradedDate?: string;

  // Additional metadata
  weightage?: number; // Weightage in final assessment
  instructions?: string;
  remarks?: string;
  academicYear?: string; // Academic year for the assessment

  actions: TActions;
}

// ============================================================
// API Request/Response Interfaces (matching backend API)
// ============================================================

export interface ISubjectScoreConfig {
  courseId: number;
  maxScore: number;
  passingScore: number;
  scheduledDuration?: number; // Duration in minutes (for IAssessmentSubject)
  scheduledDate?: string; // Scheduled date for this subject
}

export interface IAssessmentRequest {
  assessment: {
    instituteId: string;
    name: string;
    description?: string;
    assessmentType:
    | 'quiz'
    | 'assignment'
    | 'exam'
    | 'project'
    | 'midterm'
    | 'final'
    | 'class_test'
    | 'homework';
    status?: 'draft' | 'scheduled' | 'active' | 'completed' | 'graded';
    classId?: string;
    courseId?: string;
    maxScore: number;
    passingScore?: number; // Global passing score
    dueDate?: string;
    weightage?: number;
    instructions?: string;
    remarks?: string; // Assessment-level remarks
    academicYear?: string;
    subjectIds?: number[]; // Selected subject IDs
    studentIds?: string[]; // Selected student IDs (empty array means all students)
    subjectScoreConfigs?: ISubjectScoreConfig[]; // Per-subject score configuration for IStudentSubjectAssessment
  };
}

export interface IUpdateSubjectScoresRequest {
  subject: {
    courseId: number;
    scores: Array<{
      studentId: string;
      score: number;
      isApplicableForGrading?: boolean;
      review?: string; // Optional review/remark for individual student score
    }>;
    remarks?: string; // Optional overall remarks for all scores
  };
}

export interface IAssessmentResponse {
  id: string;
  name: string;
  description?: string;
  assessmentType: string;
  status: string;
  class?: {
    id: string;
    name?: string;
    section?: string;
  };
  course?: {
    id: string;
    name?: string;
    subjectCode?: string;
  };
  maxScore: number;
  totalStudents?: number;
  averageScore?: number;
  averagePercentage?: number;
  scheduledDate?: string;
  conductedDate?: string;
  dueDate?: string;
  gradedDate?: string;
  weightage?: number;
  instructions?: string;
  academicYear?: string;
  institute: {
    id: string;
    name?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface IAssessmentListQueryParams {
  page?: number;
  limit?: number;
  instituteId?: string;
  classId?: string;
  courseId?: string;
  academicYear?: string;
  assessmentType?: string;
  status?: string;
}

export interface IStudentScore {
  studentDetails: TPrimaryDetails;
  score: number;
  isApplicableForGrading: boolean;
  review?: string; // Optional review/remark for individual student score
}

// Report card entry for a student
export interface IAssessmentReportCard {
  studentDetails: TPrimaryDetails;
  reportCard?: TDocument; // Report card document if generated
}

// Assessment subject interface - represents a subject within an assessment
export interface IAssessmentSubject {
  courseId: number;
  courseName: string;
  subjectCode?: string;
  subjectType?: string;
  scheduledDuration?: number; // Duration in minutes
  scheduledDate?: string; // Scheduled date for this subject
  remarks?: string; // Subject-level remarks
  scores: IStudentScore[]; // Student scores for this subject
  // Subject-level statistics (optional, can be computed)
  totalStudents?: number;
  averageScore?: number;
  averagePercentage?: number;
}

// Assessment aggregated view for class (similar to TStudentScoreMap)
export interface IAcademicsAssessmentClassMap {
  id: number; // Class ID
  classDetails: {
    name: string;
    standardLevel: string;
    section?: string;
    strength?: number;
  };
  assessments: {
    [key in EAssessmentType]?: IAcademicsAssessment[];
  };
  totalAssessments: number;
  averageScore: number;
  averagePercentage: number;
  completionRate: number; // Percentage of assessments completed
}

// Assessment aggregated view for course (similar to TStudentScoreMap)
export interface IAcademicsAssessmentCourseMap {
  id: number; // Course ID
  courseDetails: {
    name: string;
    subjectCode: string;
    subjectType: string;
    classIds: number[]; // Classes where this course is taught
  };
  assessments: {
    [key in EAssessmentType]?: IAcademicsAssessment[];
  };
  totalAssessments: number;
  averageScore: number;
  averagePercentage: number;
  completionRate: number; // Percentage of assessments completed
}

// Assessment statistics for summary cards
export interface IAcademicsAssessmentStatistics {
  totalAssessments: number;
  totalClasses: number;
  totalCourses: number;
  averageScore: number;
  averagePercentage: number;
  completionRate: number; // Overall completion rate
  scheduledAssessments: number; // Upcoming assessments
  completedAssessments: number;
  inProgressAssessments: number;
  assessmentsByType: {
    [key in EAssessmentType]?: number;
  };
}

// View type enum for filtering
export enum EAssessmentViewType {
  CLASSES = 'classes',
  COURSES = 'courses',
}

// Response interfaces for API
export interface IAcademicsAssessmentsResponse {
  assessments: IAcademicsAssessment[];
  statistics?: IAcademicsAssessmentStatistics;
}

export interface IAcademicsAssessmentClassMapResponse {
  classAssessments: IAcademicsAssessmentClassMap[];
  statistics?: IAcademicsAssessmentStatistics;
}

export interface IAcademicsAssessmentCourseMapResponse {
  courseAssessments: IAcademicsAssessmentCourseMap[];
  statistics?: IAcademicsAssessmentStatistics;
}

export interface IAcademicsAssessmentDetails extends IAcademicsAssessment {
  subjects: IAssessmentSubject[]; // Multiple subjects under this assessment, each with their scores
  reportCards?: IAssessmentReportCard[]; // Report cards list, one per student (sibling of subjects)
  // Computed dates based on subjects' scheduled dates
  scheduledStartDate?: string; // Computed from first subject's scheduledDate (read-only)
  scheduledEndDate?: string; // Computed from last subject's scheduledDate (read-only)
}
